import React, { useState, useEffect } from 'react';
import {
    Users, FileText, Activity, Layers, UserPlus, CreditCard,
    Send, Zap, MessageCircle, LayoutDashboard, Settings,
    TrendingUp, MessageSquare, ExternalLink, Table, ShieldCheck
} from 'lucide-react';
import UserProvisioningModal from './UserProvisioningModal';
import LeadCaptureModal from './LeadCaptureModal';
import ConversionModal from './ConversionModal';
import BroadcastModal from './BroadcastModal';
import InvoiceModal from './InvoiceModal';
import LogPaymentModal from './LogPaymentModal';
import LogExpenseModal from './LogExpenseModal';
import StatementModal from './StatementModal';
import ContentManagerModal from './ContentManagerModal';
import { SystemHealthWidget } from './AdminWidgets';
import CompassWidget from './CompassWidget';
import { db } from '../../db';
import { config } from '../../config';
import AdminChatConsole from './AdminChatConsole';
import { syncManager } from '../../services/sync';

const AdminDashboard = ({ adminDataLinks = [], adminOSLinks = [], standardToolLinks = [], onNavigate }) => {
    const [activeModal, setActiveModal] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [currentUser, setCurrentUser] = useState(null);

    useEffect(() => {
        const email = localStorage.getItem(config.USER_EMAIL_KEY);
        const role = localStorage.getItem(config.USER_ROLE_KEY);
        if (email && role) {
            setCurrentUser({ email, role });
        }
    }, []);


    // Track open state for high-level sections. 
    // Keys: 'compass', 'admin_os', 'data_tracker', 'master_tables', 'virtual_tools'
    const [openSections, setOpenSections] = useState({
        compass: true,
        admin_os: true,
        data_tracker: false,
        master_tables: false,
        virtual_tools: false
    });

    const toggleSection = (key) => {
        setOpenSections(prev => ({ ...prev, [key]: !prev[key] }));
    };

    // Functional Admin Actions
    const handleAction = async (action) => {
        console.log(`Triggering Admin Action: ${action}`);

        // Actions that open specialized modals
        const modalActions = [
            'provision_user',
            'capture_lead',
            'convert_user',
            'broadcast',
            'create_invoice',
            'create_invoice',
            'log_payment',
            'log_expense',
            'gen_statement',
            'push_updates'
        ];

        if (modalActions.includes(action)) {
            setActiveModal(action);
            return;
        }

        // Direct Execution Actions
        setIsProcessing(true);
        try {
            switch (action) {
                case 'gen_pnl':
                    if (confirm('Generate P&L Report for the current month?')) {
                        await syncManager.triggerWorkflow('hq-gen-monthly-report', { Month: new Date().toISOString().substring(0, 7) });
                        alert('P&L Generation triggered! Check your email/Hub shortly.');
                    }
                    break;
                case 'log_focus':
                    if (confirm('Log Admin Focus and reset for today?')) {
                        await syncManager.triggerWorkflow('hq-pwa-log-focus', { timestamp: new Date().toISOString() });
                        alert('Admin Focus logged.');
                    }
                    break;
                default:
                    alert(`Action ${action} is pending backend port.`);
            }
        } catch (e) {
            console.error("Action failed", e);
            alert(`Execution failed: ${e.message}`);
        } finally {
            setIsProcessing(false);
        }
    };

    const sections = [
        {
            title: '👥 User Management',
            color: 'bg-blue-50 text-blue-700',
            icon: Users,
            items: [
                { label: 'Add User / Provision Sheet', icon: UserPlus, action: 'provision_user' },
                { label: 'Conversion / Upgrade User', icon: Layers, action: 'convert_user' },
                { label: 'Log Admin Focus', icon: Activity, action: 'log_focus' }
            ]
        },
        {
            title: '💰 Finance & Operations',
            color: 'bg-green-50 text-green-700',
            icon: CreditCard,
            items: [
                { label: 'Smart Invoice Creator', icon: FileText, action: 'create_invoice' },
                { label: 'Log Manual Payment', icon: CreditCard, action: 'log_payment' },
                { label: 'Log Business Expense', icon: TrendingUp, action: 'log_expense' },
                { label: 'Generate Monthly P&L', icon: Activity, action: 'gen_pnl' },
                { label: 'Generate Statement', icon: FileText, action: 'gen_statement' }
            ]
        },
        {
            title: '📢 CRM & Comms',
            color: 'bg-purple-50 text-purple-700',
            icon: MessageCircle,
            items: [
                { label: 'Capture Lead', icon: UserPlus, action: 'capture_lead' },
                { label: 'Broadcast WhatsApp', icon: Send, action: 'broadcast' },
                { label: 'Send Document', icon: Send, action: 'send_doc' }
            ]
        },
        {
            title: '⚡ System Tools',
            color: 'bg-amber-50 text-amber-700',
            icon: Zap,
            items: [
                { label: 'Content Push Updates', icon: Zap, action: 'push_updates' }
            ]
        }
    ];

    return (
        <div className="space-y-8 animate-in fade-in duration-500">
            <div className="bg-slate-900 text-white rounded-[2.5rem] p-8 shadow-xl relative overflow-hidden">
                <div className="relative z-10">
                    <h1 className="text-3xl font-black tracking-tight mb-2">Admin OS</h1>
                    <p className="text-slate-400 font-medium">Control Center • {new Date().toLocaleDateString()}</p>
                </div>
                <div className="absolute top-0 right-0 w-64 h-64 bg-blue-600 rounded-full blur-[100px] opacity-20 -mr-20 -mt-20 pointer-events-none" />
            </div>

            {/* 1. THE COMPASS */}
            <SectionCard
                title="The Compass"
                subtitle="Business Intelligence & KPIs"
                icon={TrendingUp}
                isOpen={openSections.compass}
                onToggle={() => toggleSection('compass')}
            >
                <CompassWidget />
            </SectionCard>

            {/* 2. ADMIN OS (System Health + Action Menus) */}
            <SectionCard
                title="Admin OS"
                subtitle="System Health & Operations"
                icon={Settings}
                isOpen={openSections.admin_os}
                onToggle={() => toggleSection('admin_os')}
            >
                <div className="flex flex-col gap-6">
                    <SystemHealthWidget />

                    {/* Collapsible Sub-Menus (Actions) */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {sections.map((section, idx) => (
                            <div key={idx} className="bg-slate-50 border border-slate-200 rounded-2xl p-4">
                                <div className="flex items-center gap-3 mb-4">
                                    <div className={`p-2 rounded-lg ${section.color}`}>
                                        <section.icon size={18} />
                                    </div>
                                    <h3 className="font-bold text-slate-700">{section.title}</h3>
                                </div>
                                <div className="space-y-2">
                                    {section.items.map((item, i) => (
                                        <button
                                            key={i}
                                            onClick={() => handleAction(item.action)}
                                            className="w-full flex items-center justify-between p-3 rounded-xl bg-white border border-slate-100 hover:border-blue-300 hover:shadow-sm text-left group transition-all"
                                        >
                                            <span className="text-sm font-medium text-slate-600 group-hover:text-slate-900">{item.label}</span>
                                            <div className="bg-slate-100 px-2 py-0.5 rounded text-[9px] font-bold text-slate-400 group-hover:bg-blue-600 group-hover:text-white uppercase">Run</div>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </SectionCard>

            {/* 3. SYSTEM CONTROL */}
            <SectionCard
                title="System Control"
                subtitle="Live Admin Data Tables"
                icon={MessageSquare}
                isOpen={openSections.data_tracker}
                onToggle={() => toggleSection('data_tracker')}
            >
                <div className="space-y-6">
                    {adminDataLinks.length > 0 && (
                        <div>
                            <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 mb-3 ml-1">Native Micro-Service Audit</h4>
                            <CompactLinkGrid items={adminDataLinks} onNavigate={onNavigate} />
                        </div>
                    )}
                    <div>
                        <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 mb-3 ml-1">Live Management Chat Console</h4>
                        {currentUser ? (
                            <AdminChatConsole currentUser={currentUser} />
                        ) : (
                            <div className="p-8 text-center text-slate-400 font-medium bg-slate-50 rounded-2xl border border-dashed border-slate-200">Loading Chat Console...</div>
                        )}
                    </div>
                </div>
            </SectionCard>

            {/* 4. PRODUCTION INFRASTRUCTURE */}
            <SectionCard
                title="Admin Production Infrastructure"
                subtitle="Direct Sovereign Admin Production Tables Access"
                icon={LayoutDashboard}
                isOpen={openSections.master_tables}
                onToggle={() => toggleSection('master_tables')}
            >
                {adminOSLinks.length > 0 ? (
                    <CompactLinkGrid items={adminOSLinks} onNavigate={onNavigate} />
                ) : (
                    <div className="p-8 text-center text-slate-400 font-medium bg-slate-50 rounded-2xl border border-dashed border-slate-200">
                        No production tables identified in schema.
                    </div>
                )}
            </SectionCard>

            {/* 5. SATELLITE MODULES */}
            <SectionCard
                title="Satellite Modules"
                subtitle="External Logic & Automation Engines"
                icon={Zap}
                isOpen={openSections.virtual_tools}
                onToggle={() => toggleSection('virtual_tools')}
            >
                <div className="space-y-6">
                    {standardToolLinks.length > 0 && (
                        <CompactLinkGrid items={standardToolLinks} onNavigate={onNavigate} />
                    )}

                    <div>
                        <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 mb-3 ml-1">External Infrastructure</h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                            <ExternalToolLink
                                label="n8n Automation Engine"
                                url={config.API_BASE_URL}
                                icon={Zap}
                                color="text-amber-600 bg-amber-50"
                            />
                            <ExternalToolLink
                                label="WAHA Bridge"
                                url={`${config.API_BASE_URL}/waha/`}
                                icon={MessageSquare}
                                color="text-blue-600 bg-blue-50"
                            />
                            <ExternalToolLink
                                label="Cloudflare Dash"
                                url="https://dash.cloudflare.com/"
                                icon={ShieldCheck}
                                color="text-orange-600 bg-orange-50"
                            />
                        </div>
                    </div>
                </div>
            </SectionCard>

            {/* Active Modals */}
            {activeModal === 'provision_user' && (
                <UserProvisioningModal onClose={() => setActiveModal(null)} />
            )}
            {activeModal === 'capture_lead' && (
                <LeadCaptureModal onClose={() => setActiveModal(null)} />
            )}
            {activeModal === 'convert_user' && (
                <ConversionModal onClose={() => setActiveModal(null)} />
            )}
            {activeModal === 'broadcast' && (
                <BroadcastModal onClose={() => setActiveModal(null)} />
            )}
            {activeModal === 'create_invoice' && (
                <InvoiceModal onClose={() => setActiveModal(null)} />
            )}
            {activeModal === 'log_payment' && (
                <LogPaymentModal onClose={() => setActiveModal(null)} />
            )}
            {activeModal === 'log_expense' && (
                <LogExpenseModal onClose={() => setActiveModal(null)} />
            )}
            {activeModal === 'gen_statement' && (
                <StatementModal onClose={() => setActiveModal(null)} />
            )}
            {activeModal === 'push_updates' && (
                <ContentManagerModal onClose={() => setActiveModal(null)} />
            )}

            {/* Placeholder for other modals */}
            {activeModal && ![
                'provision_user', 'capture_lead', 'convert_user', 'broadcast',
                'create_invoice', 'log_payment', 'log_expense', 'gen_statement', 'push_updates'
            ].includes(activeModal) && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
                        <div className="bg-white rounded-3xl p-8 max-w-sm text-center">
                            <h3 className="text-lg font-bold mb-2">Execute: {activeModal}</h3>
                            <p className="text-slate-500 mb-6 text-sm">This feature is being ported. Check back soon.</p>
                            <button onClick={() => setActiveModal(null)} className="w-full bg-slate-900 text-white py-3 rounded-xl">Close</button>
                        </div>
                    </div>
                )}
        </div>
    );
};



// Reusable External Tool Link
const ExternalToolLink = ({ label, url, icon: Icon, color }) => (
    <a
        href={url}
        target="_blank"
        rel="noreferrer"
        className="flex items-center justify-between p-4 rounded-2xl bg-white border border-slate-100 hover:border-blue-400 hover:shadow-md transition-all group"
    >
        <div className="flex items-center gap-3">
            <div className={`p-2 rounded-xl ${color}`}>
                <Icon size={18} />
            </div>
            <span className="text-sm font-bold text-slate-700 group-hover:text-slate-900">{label}</span>
        </div>
        <ExternalLink size={14} className="text-slate-300 group-hover:text-blue-500 transition-colors" />
    </a>
);

// Reusable Compact Link Grid
const CompactLinkGrid = ({ items, onNavigate }) => (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {items.map((link) => (
            <div
                key={link.id}
                onClick={() => onNavigate && onNavigate(link.id)}
                className="group cursor-pointer flex items-center justify-between p-4 rounded-2xl bg-white border border-slate-100 hover:border-blue-400 hover:shadow-md transition-all"
            >
                <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-xl ${link.color || 'bg-slate-100 text-slate-500 shadow-sm'}`}>
                        {link.icon ? <link.icon size={18} /> : <Table size={18} />}
                    </div>
                    <div>
                        <h4 className="text-sm font-bold text-slate-800 group-hover:text-blue-700 transition-colors truncate max-w-[120px]">
                            {link.label}
                        </h4>
                        <p className="text-[9px] font-black uppercase text-slate-400 tracking-wider">Show View</p>
                    </div>
                </div>
                <div className="flex gap-1">
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            if (link.url) {window.open(link.url, '_blank');}
                        }}
                        className="p-1.5 rounded-lg hover:bg-emerald-50 text-slate-300 hover:text-emerald-600 transition-all"
                    >
                        <ExternalLink size={14} />
                    </button>
                </div>
            </div>
        ))}
    </div>
);



// Reusable Section Card Component
const SectionCard = ({ title, subtitle, icon: Icon, isOpen, onToggle, children }) => (
    <div className="bg-white border border-slate-100 rounded-[2.5rem] shadow-sm overflow-hidden transition-all duration-300 hover:shadow-lg">
        <button
            onClick={onToggle}
            className="w-full flex items-center justify-between p-8 hover:bg-slate-50/50 transition-colors text-left"
        >
            <div className="flex items-center gap-5">
                <div className={`p-4 rounded-2xl ${isOpen ? 'bg-blue-600 text-white shadow-lg shadow-blue-200' : 'bg-slate-100 text-slate-500'} transition-all duration-300`}>
                    <Icon size={24} />
                </div>
                <div>
                    <h2 className="text-xl font-black text-slate-800 tracking-tight">{title}</h2>
                    <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mt-1">{subtitle}</p>
                </div>
            </div>
            <div className={`p-3 rounded-full bg-slate-50 text-slate-400 transition-transform duration-300 border border-slate-100 ${isOpen ? 'rotate-180 bg-white shadow-sm' : ''}`}>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6" /></svg>
            </div>
        </button>

        <div className={`transition-all duration-500 ease-in-out ${isOpen ? 'max-h-[600px] opacity-100 border-t border-slate-100' : 'max-h-0 opacity-0 overflow-hidden'}`}>
            <div className={`p-8 pt-6 ${isOpen ? 'overflow-y-auto max-h-[600px] scrollbar-thin scrollbar-thumb-slate-200' : ''}`}>
                {children}
            </div>
        </div>
    </div>
);

export default AdminDashboard;
